// The "why" layer: the validator's own drawing on top of the plan.
//
// This adds nothing to the plan and re-draws none of it. The three views
// (PlanView, ApartmentView, SitePlanView) render the building exactly as before,
// the existing selection highlight lights the element the instance points at, and
// this layer draws only the reasoning: the dimension that was measured, the route
// that was walked, the stretch that pinched.
//
// It mounts as the last child of Editor's transform `<g>`, so its metre
// coordinates need no conversion — the same slot and the same trick as the
// measuring tool, which is also the visual template for the dimension style
// (dashed line, end ticks, halo label via `paintOrder`).
//
// Every element carries `style={DECOR}` (pointer-events: none). Without it the
// annotation sits over the very shape the user is trying to click and
// `selectionFromTarget`'s `closest("[data-pick]")` walks up from the annotation
// instead of the glyph — the plan would go inert exactly where it is most
// interesting.

import { DECOR } from "./glyphs";
import type { VAnno, VStatus } from "../validation";

/** One colour per verdict. Red and green are the two verdicts; amber is "someone
 *  must confirm this", which is neither a pass nor a failure and must not be
 *  drawn as either; slate is for a rule that does not apply here, where a
 *  coloured annotation would imply a judgement that was not made. */
const COLOUR: Record<VStatus, string> = {
  neconform: "#dc2626",
  conform: "#16a34a",
  necesita_confirmare: "#d97706",
  inaplicabil: "#64748b",
  neverificabil: "#94a3b8",
};

const ringPath = (ring: ReadonlyArray<ReadonlyArray<number>>) =>
  ring.length
    ? `M${ring.map((p) => `${p[0]} ${p[1]}`).join(" L")} Z`
    : "";

/** A text label with a white halo, so it stays readable over hatching, furniture
 *  and wall lines alike. Font size is in metres because the whole drawing is:
 *  1 unit = 1 m under the Editor's scale-only transform. */
function Label({ x, y, text, colour, anchor = "middle" }: {
  x: number; y: number; text: string; colour: string;
  anchor?: "middle" | "start" | "end";
}) {
  return (
    <text
      x={x} y={y} fontSize={0.7} textAnchor={anchor} fill={colour}
      style={{
        ...DECOR, fontWeight: 600,
        paintOrder: "stroke", stroke: "#ffffff", strokeWidth: 0.42,
      }}
    >
      {text}
    </text>
  );
}

/** The measurement itself: a line across what was measured, ticked at both ends
 *  so it reads as a dimension and not as a wall, with the number on a leader
 *  outside the space.
 *
 *  The number was first centred ON the line. It is a haloed label about 0,7 m
 *  tall and the thing being measured is 0,80 m wide, so the halo wiped out the
 *  dimension line, the tinted stretch and the walls either side — the annotation
 *  hid its own evidence. Leading it out into the neighbouring room is how the
 *  same dimension is drawn by hand when the space is too tight to letter in. */
function Dim({ a, b, label, colour }: {
  a: [number, number]; b: [number, number]; label: string; colour: string;
}) {
  const [x1, y1] = a;
  const [x2, y2] = b;
  const dx = x2 - x1, dy = y2 - y1;
  const L = Math.hypot(dx, dy) || 1;
  // Unit normal: the end ticks lie along it, and the leader runs out along it.
  //
  // The SIGN is forced when the normal is mostly horizontal, because otherwise it
  // comes from the winding of the two points the server happened to send — which
  // is arbitrary, and half the time led the label leftwards, underneath the
  // drawer. The drawer is docked left and now stays open while you look at the
  // plan, so leftwards is the one direction that can be occluded; rightwards is
  // always visible. A near-vertical normal is left alone: up and down are both
  // free, and flipping it would only move which room the label lands in.
  let nx = -dy / L, ny = dx / L;
  if (Math.abs(nx) > Math.abs(ny) && nx < 0) { nx = -nx; ny = -ny; }
  const tick = 0.26;
  const lead = Math.max(1.9, L * 0.6 + 1.4);
  const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
  const lx = mx + nx * lead, ly = my + ny * lead;
  // Anchor away from the leader so the text runs outward, not back over the line.
  const anchor = Math.abs(nx) < 0.4 ? "middle" : nx < 0 ? "end" : "start";
  const dyText = Math.abs(nx) < 0.4 ? (ny < 0 ? -0.25 : 0.7) : 0.24;
  return (
    <g style={DECOR}>
      {/* white casing, so the dimension reads over a dark wall as well as over
          a pale floor */}
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#ffffff" strokeWidth={5}
            vectorEffect="non-scaling-stroke" style={DECOR} />
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={colour} strokeWidth={2.4}
            vectorEffect="non-scaling-stroke" style={DECOR} />
      {[a, b].map(([px, py], i) => (
        <line key={i} x1={px - nx * tick} y1={py - ny * tick}
              x2={px + nx * tick} y2={py + ny * tick}
              stroke={colour} strokeWidth={2.4}
              vectorEffect="non-scaling-stroke" style={DECOR} />
      ))}
      <line x1={mx} y1={my} x2={lx} y2={ly} stroke={colour} strokeWidth={1.2}
            strokeDasharray="3 2" vectorEffect="non-scaling-stroke" style={DECOR} />
      <Label x={lx + nx * 0.22} y={ly + dyText} text={label} colour={colour}
             anchor={anchor} />
    </g>
  );
}

/** A walked route. Dashed, arrowed at the far end, drawn down the middle of the
 *  corridor by the server so it reads as a path a person takes.
 *
 *  The label goes above the `region` — the stretch where the route pinches —
 *  rather than at the route's midpoint, because the pinch is the answer and the
 *  midpoint is wherever the geometry happened to put it. */
function Path({ pts, label, colour, region }: {
  pts: [number, number][]; label: string; colour: string;
  region?: [number, number][];
}) {
  if (pts.length < 2) return null;
  const d = `M${pts.map((p) => `${p[0]} ${p[1]}`).join(" L")}`;
  const [hx, hy] = pts[pts.length - 1];
  const [px, py] = pts[pts.length - 2];
  const ang = (Math.atan2(hy - py, hx - px) * 180) / Math.PI;
  // Label above the pinch when there is one: that stretch is the answer, and the
  // route's own midpoint is wherever the geometry happened to put it.
  const mid: [number, number] = region && region.length > 2
    ? [region.reduce((a, p) => a + p[0], 0) / region.length,
       Math.min(...region.map((p) => p[1]))]
    : pts[Math.floor(pts.length / 2)];
  return (
    <g style={DECOR}>
      <path d={d} fill="none" stroke="#ffffff" strokeWidth={5}
            vectorEffect="non-scaling-stroke" style={DECOR} />
      <path d={d} fill="none" stroke={colour} strokeWidth={2.4} strokeDasharray="5 3"
            vectorEffect="non-scaling-stroke" style={DECOR} />
      <circle cx={pts[0][0]} cy={pts[0][1]} r={0.22} fill={colour} style={DECOR} />
      {/* arrow head, drawn in metres and rotated onto the last leg */}
      <polygon points="0,0 -0.75,0.34 -0.75,-0.34" fill={colour} style={DECOR}
               transform={`translate(${hx} ${hy}) rotate(${ang})`} />
      <Label x={mid[0]} y={mid[1] - 0.75} text={label} colour={colour} />
    </g>
  );
}

/** Discrete places, one marker each: where two lanes conflict, where a mirror
 *  goes. Not a polyline — see `anno_marks` in validation/model.py for why the
 *  server keeps the two apart.
 *
 *  Numbered, because the instance's detail text lists the same coordinates in the
 *  same order and the number is what ties a sentence to a spot on the drawing.
 *  Above `NUMBER_LIMIT` marks the numbers are dropped and the markers stay: forty
 *  haloed numerals on a garage plan hide the lanes they are annotating, and past
 *  that count nobody is reading the list item by item anyway.
 *
 *  Drawn as a ring plus a cross rather than a filled dot: a dot the size of a
 *  conflict point (about 1 m across at garage scale) covers the very lane
 *  intersection it is pointing at. The ring leaves the geometry visible through
 *  the middle of it. */
const NUMBER_LIMIT = 24;

function Marks({ pts, label, colour, segs = [] }: {
  pts: [number, number][]; label: string; colour: string;
  segs?: [number, number][][];
}) {
  if (!pts.length) return null;
  const r = 0.62;
  const c = r * 0.62;              // the cross, kept inside the ring
  // The label goes over the topmost mark, so it never lands in the middle of the
  // cluster on top of another marker.
  const top = pts.reduce((a, p) => (p[1] < a[1] ? p : a), pts[0]);
  return (
    <g style={DECOR}>
      {/* The network first, under everything: the aisle centrelines the marks were
          derived from. Thin, dashed and half-transparent — this is the reason for
          the marks, not a second set of them, and it has to stay legible over the
          bays it runs between. Drawn BEFORE the markers so a marker is never
          hidden by the line it sits on. */}
      {segs.map((s, i) => (
        <polyline key={`s${i}`} points={s.map((p) => `${p[0]},${p[1]}`).join(" ")}
                  fill="none" stroke={colour} strokeOpacity={0.5} strokeWidth={1.4}
                  strokeDasharray="6 4" vectorEffect="non-scaling-stroke"
                  style={DECOR} />
      ))}
      {pts.map(([x, y], i) => (
        <g key={i} style={DECOR}>
          <circle cx={x} cy={y} r={r} fill="#ffffff" fillOpacity={0.55}
                  stroke="#ffffff" strokeWidth={5}
                  vectorEffect="non-scaling-stroke" style={DECOR} />
          <circle cx={x} cy={y} r={r} fill="none" stroke={colour} strokeWidth={2.4}
                  vectorEffect="non-scaling-stroke" style={DECOR} />
          <line x1={x - c} y1={y - c} x2={x + c} y2={y + c} stroke={colour}
                strokeWidth={1.8} vectorEffect="non-scaling-stroke" style={DECOR} />
          <line x1={x - c} y1={y + c} x2={x + c} y2={y - c} stroke={colour}
                strokeWidth={1.8} vectorEffect="non-scaling-stroke" style={DECOR} />
          {pts.length <= NUMBER_LIMIT && (
            <Label x={x + r + 0.28} y={y - r - 0.1} text={String(i + 1)}
                   colour={colour} anchor="start" />
          )}
        </g>
      ))}
      {/* Clear of the topmost marker's own number, which sits at r + 0.1 above
          it — at 0,95 m the two labels touched. Still inside the 2,5 m padding
          `_pts_bbox` leaves, so framing the instance never crops the caption. */}
      <Label x={top[0]} y={top[1] - r - 1.6} text={label} colour={colour} />
    </g>
  );
}

export interface AnnotationLayerProps {
  anno: VAnno | null;
  status: VStatus;
  /** Rings to tint faintly: the geometry of the instance's `context` selections,
   *  resolved by the Editor (which is what holds the plan). Passing rings rather
   *  than selections keeps this component free of plan lookups. */
  contextRings?: ReadonlyArray<ReadonlyArray<ReadonlyArray<number>>>;
  /** The ring of the element the instance points at, when the annotation is an
   *  outline of it — the server omits that polygon precisely because the UI has
   *  it. */
  refRing?: ReadonlyArray<ReadonlyArray<number>> | null;
}

export default function AnnotationLayer(
  { anno, status, contextRings = [], refRing = null }: AnnotationLayerProps,
) {
  if (!anno) return null;
  const colour = COLOUR[status] ?? COLOUR.inaplicabil;

  return (
    <g style={DECOR} data-annotation="validator">
      {/* faint tint on related elements, so the reason has its context */}
      {contextRings.map((ring, i) => (
        <path key={`ctx${i}`} d={ringPath(ring)} fill={colour} fillOpacity={0.07}
              stroke={colour} strokeOpacity={0.35} strokeWidth={1.2}
              strokeDasharray="3 2" vectorEffect="non-scaling-stroke" style={DECOR} />
      ))}

      {/* The stretch, or the pinch point on a route. Tinted AND outlined: at 16%
          over a pale floor the tint alone was invisible, and this shape is the
          answer to "which part of the hall is too narrow".

          A path, not a <rect>: a stretch is a rectangle in its building's frame,
          so on a block at 43° it is a rotated quadrilateral and an axis-aligned
          rect around it would tint the neighbouring rooms too. */}
      {anno.region && anno.region.length > 2 && (
        <path d={ringPath(anno.region)}
              fill={colour} fillOpacity={0.24}
              stroke={colour} strokeWidth={1.6} strokeDasharray="4 2.5"
              vectorEffect="non-scaling-stroke" style={DECOR} />
      )}

      {anno.type === "outline" && (() => {
        const ring = anno.poly ?? refRing;
        return (
          <g style={DECOR}>
            {ring && (
              <path d={ringPath(ring)} fill={colour} fillOpacity={0.1}
                    stroke={colour} strokeWidth={2.5}
                    vectorEffect="non-scaling-stroke" style={DECOR} />
            )}
            {ring && (() => {
              const xs = ring.map((p) => p[0]);
              const ys = ring.map((p) => p[1]);
              const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
              return <Label x={cx} y={Math.min(...ys) - 0.5} text={anno.label} colour={colour} />;
            })()}
          </g>
        );
      })()}

      {anno.type === "dim" && anno.a && anno.b && (
        <Dim a={anno.a} b={anno.b} label={anno.label} colour={colour} />
      )}

      {anno.type === "path" && anno.pts && (
        <Path pts={anno.pts} label={anno.label} colour={colour} region={anno.region} />
      )}

      {anno.type === "marks" && anno.pts && (
        <Marks pts={anno.pts} label={anno.label} colour={colour} segs={anno.segs} />
      )}
    </g>
  );
}
